import { ConfigField, ConfigSlideId } from '@/eventConfig/types.ts';
import React, { FC, KeyboardEventHandler, MouseEventHandler } from 'react';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { FormGetFieldStatusFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import rehypeExternalLinks from 'rehype-external-links';
import { useUiEvent } from '@/util/useUiEvent.ts';

interface RichtextProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
}

export const RichtextInput: FC<RichtextProps> = ({ field }) => {
  const htmlId = 'id_field_' + field.name;
  const { handleUiEvent } = useUiEvent();

  const getExternalAnchor = (root: HTMLElement) => {
    const links = root.querySelectorAll<HTMLAnchorElement>('a[href]');

    for (const link of links) {
      if (link.target === '_blank') {
        return link;
      }

      try {
        const url = new URL(link.href, window.location.href);
        if (url.origin !== window.location.origin) {
          return link;
        }
      } catch {
        continue;
      }
    }

    return null;
  };

  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = () => {
    if (field.click && handleUiEvent) {
      handleUiEvent(field.click);
    }
  };

  const handleRichtextKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    const isActivationKey =
      event.key === 'Enter' || event.code === 'NumpadEnter' || event.key === ' ' || event.key === 'Spacebar';

    if (!isActivationKey) {
      return;
    }

    const link = getExternalAnchor(event.currentTarget);
    if (!link) {
      return;
    }

    event.preventDefault();
    link.click();
  };

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
  ];

  if (!field.label) {
    return null;
  }

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type} tabIndex={0} onKeyDown={handleRichtextKeyDown}>
        <label htmlFor={htmlId} onClick={handleLabelClick}>
          <ReactMarkdown rehypePlugins={[[rehypeExternalLinks, { target: '_blank' }]]}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>
      </div>
    </div>
  );
};
